/**
 * The Admin console's navigation.
 *
 * ── AN ITEM EXISTS HERE ONLY IF THE SCREEN EXISTS ────────────────────────────────────
 *
 * The frozen architecture describes a far larger hierarchy than Voleto has built:
 * Admissions, Sponsor CRM, Pages and Templates, Workflows, Integrations, and more. None
 * of those appear below, because a menu item that leads to a placeholder teaches an
 * operator to distrust the menu — and at the scale that hierarchy implies, half a sidebar
 * of dead links is worse than a short one that is entirely true.
 *
 * Every GROUP heading from the target hierarchy is kept where at least one real screen
 * lives under it. The unbuilt groups (SPONSORS, CONTENT & DESIGN) are absent entirely;
 * the target list for each partial group is recorded in `plannedButNotBuilt` so the next
 * task can see what is missing without reading the architecture document.
 *
 * ── PERMISSION ───────────────────────────────────────────────────────────────────────
 *
 * Every route in this console is behind requireAdmin on the engine, and the whole subtree
 * is behind a platform-admin route guard in the browser. So the visibility predicate has
 * one input today and one answer. It exists as a predicate rather than as `true` because
 * Task 5's memberships will eventually let an organization admin reach a subset of this
 * console, and the place that decision belongs is here — not in twenty components.
 *
 * Hiding an item is a UX decision and NEVER a security one. The engine authorizes every
 * request regardless of what this file renders.
 */

export interface AdminNavItem {
  to: string;
  label: string;
  /** Match the exact path only — for a group's index route. */
  end?: boolean;
  /** Visibility. Absent means "visible to anyone who can reach the console". */
  visible?: (ctx: AdminNavContext) => boolean;
}

export interface AdminNavGroup {
  title: string;
  items: AdminNavItem[];
  /** Target-hierarchy items with no implementation yet. Documentation, never rendered. */
  plannedButNotBuilt?: string[];
}

export interface AdminNavContext {
  isPlatformAdmin: boolean;
}

export const ADMIN_NAV: AdminNavGroup[] = [
  {
    title: 'Overview',
    items: [{ to: '/admin', label: 'Dashboard', end: true }],
  },
  {
    title: 'Events',
    items: [
      { to: '/admin/events', label: 'All events', end: true },
      { to: '/admin/events/new', label: 'Create event' },
      { to: '/admin/orders', label: 'Orders' },
      { to: '/admin/compliance', label: 'Compliance' },
    ],
    // Admissions and Check-In have engine support (task 3) but no built admin screen:
    // the portal's check-in routes are placeholders, so they are not listed.
    plannedButNotBuilt: ['Admissions', 'Check-In'],
  },
  {
    title: 'Rentals & venues',
    items: [
      { to: '/admin/rentals', label: 'Rental pipeline', end: true },
      { to: '/admin/venues', label: 'Venue approvals', end: true },
      { to: '/admin/spaces', label: 'Spaces' },
      { to: '/admin/rentals/calendar', label: 'Calendar' },
      { to: '/admin/rentals/rate-card', label: 'Rate cards' },
    ],
    plannedButNotBuilt: ['Production Advance', 'Documents'],
  },
  {
    title: 'Promoters & sales',
    items: [{ to: '/admin/affiliates', label: 'Affiliates' }],
    plannedButNotBuilt: ['Organizers / Promoters', 'Affiliate Campaigns', 'Referrals'],
  },
  {
    title: 'Investors',
    items: [
      { to: '/admin/opportunities', label: 'Opportunities', end: true },
      { to: '/admin/investor-approvals', label: 'Investor approvals' },
      { to: '/admin/investments', label: 'Investments' },
      { to: '/admin/spvs', label: 'SPVs' },
      { to: '/admin/distributions', label: 'Distributions' },
      { to: '/admin/network', label: '506(b) network' },
    ],
    plannedButNotBuilt: ['Investor Leads (separate from approvals)'],
  },
  {
    title: 'Finance',
    items: [{ to: '/admin/financials', label: 'Revenue' }],
    plannedButNotBuilt: [
      'Expenses', 'Event Financials', 'Settlements', 'Payouts', 'Refunds',
    ],
  },
  {
    title: 'Content & design',
    items: [
      { to: '/admin/content/pages', label: 'Pages' },
      { to: '/admin/content/templates', label: 'Templates' },
      { to: '/admin/content/global-sections', label: 'Global sections' },
      { to: '/admin/content/themes', label: 'Themes' },
      { to: '/admin/copy', label: 'Copy' },
      { to: '/admin/media', label: 'Media' },
    ],
    // The group is now complete against the frozen hierarchy. A 'Header' global section
    // type is deliberately absent rather than planned: see docs/CMS.md for why the site
    // header stays application-owned.
    plannedButNotBuilt: [],
  },
  {
    title: 'Operations',
    items: [
      { to: '/admin/artists', label: 'Artist approvals' },
      { to: '/admin/audit', label: 'Audit log' },
      { to: '/admin/personas', label: 'Test personas' },
    ],
    plannedButNotBuilt: ['Tasks / Workflows', 'Notifications', 'Communication Timeline'],
  },
  {
    title: 'Platform',
    items: [
      { to: '/admin/organizations', label: 'Organizations' },
      { to: '/admin/users', label: 'Users' },
      { to: '/admin/plugins', label: 'Modules / plugins' },
    ],
    plannedButNotBuilt: ['Roles / Permissions', 'Integrations', 'System'],
  },
];

/** The groups this user should see, with empty groups dropped. */
export function visibleNav(ctx: AdminNavContext): AdminNavGroup[] {
  return ADMIN_NAV
    .map((group) => ({ ...group, items: group.items.filter((i) => !i.visible || i.visible(ctx)) }))
    .filter((group) => group.items.length > 0);
}

/** Every route the nav points at. Used by the routing tests to prove there are no dead items. */
export const ALL_NAV_PATHS: string[] = ADMIN_NAV.flatMap((g) => g.items.map((i) => i.to));
